// Offline write queue.
//
// On a mutation while online: fire normally.
// On a mutation while offline (navigator.onLine === false, OR a network
// failure): enqueue to IndexedDB and resolve optimistically.
// On reconnect (online event): drain the queue, one mutation at a time, in
// order. Each entry is removed only after the server returns 2xx.
//
// We expose `queueMutation` from useMutation handlers and `startReplay` to
// run once at boot.

import { get, set, del, keys as idbKeys } from 'idb-keyval';
import type { QueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

interface PendingMutation {
  id: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  /** Channels to invalidate after a successful replay. */
  invalidate: string[];
  /** When the mutation was enqueued. */
  enqueuedAt: number;
  /** Replay attempts (for backoff). */
  attempts: number;
}

const KEY_PREFIX = 'mutq:';

function id(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export async function enqueueMutation(
  m: Omit<PendingMutation, 'id' | 'enqueuedAt' | 'attempts'>,
): Promise<string> {
  const entry: PendingMutation = { ...m, id: id(), enqueuedAt: Date.now(), attempts: 0 };
  await set(`${KEY_PREFIX}${entry.id}`, entry);
  return entry.id;
}

export async function listPending(): Promise<PendingMutation[]> {
  const ks = await idbKeys();
  const out: PendingMutation[] = [];
  for (const k of ks) {
    if (typeof k === 'string' && k.startsWith(KEY_PREFIX)) {
      const v = await get<PendingMutation>(k);
      if (v) out.push(v);
    }
  }
  return out.sort((a, b) => a.enqueuedAt - b.enqueuedAt);
}

export async function clearPending(id: string): Promise<void> {
  await del(`${KEY_PREFIX}${id}`);
}

let draining = false;

async function drain(queryClient: QueryClient, onProgress?: (n: number) => void): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    const pending = await listPending();
    onProgress?.(pending.length);
    for (const m of pending) {
      try {
        switch (m.method) {
          case 'POST':
            await api.post(m.path, m.body);
            break;
          case 'PATCH':
            await api.patch(m.path, m.body);
            break;
          case 'PUT':
            await api.put(m.path, m.body);
            break;
          case 'DELETE':
            await api.delete(m.path);
            break;
        }
        await clearPending(m.id);
        for (const ch of m.invalidate) {
          queryClient.invalidateQueries({ queryKey: [ch] });
        }
      } catch (e) {
        if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
          // 4xx — the server rejected this permanently. Drop it so we don't
          // spin forever. Future: surface to a "review failed sync" UI.
          await clearPending(m.id);
          console.warn('Dropping offline mutation rejected by server', m, e);
          continue;
        }
        // 5xx or network failure: bump attempts and stop draining. Retry on
        // the next `online` event or a manual replay.
        m.attempts += 1;
        await set(`${KEY_PREFIX}${m.id}`, m);
        return;
      }
    }
    onProgress?.(0);
  } finally {
    draining = false;
  }
}

export function startQueueReplay(queryClient: QueryClient, onPendingCount?: (n: number) => void) {
  const tick = () => void drain(queryClient, onPendingCount);
  // Initial drain on boot
  tick();
  window.addEventListener('online', tick);
  // Drain when the realtime socket reports a reconnect (browser was likely
  // offline). The realtime layer dispatches a synthetic `online` event so we
  // don't need to subscribe to anything extra.
  return () => {
    window.removeEventListener('online', tick);
  };
}
